export const SEARCH_CATALOGUE_NODE_TYPE = "search_catalogue";
export const SEARCH_CATALOGUE_DEFAULT_MAXIMUM_RESULTS = 5;
export const SEARCH_CATALOGUE_MINIMUM_RESULTS = 2;
export const SEARCH_CATALOGUE_MAXIMUM_RESULTS = 30;
export const SEARCH_CATALOGUE_MODES = Object.freeze([
  { value: "filtered", label: "Filtered search" },
  { value: "all", label: "All catalogue products" },
]);
export const SEARCH_CATALOGUE_PATHS = Object.freeze([
  { id: "no_results", label: "No Results" },
  { id: "single_result", label: "Single Result" },
  { id: "multiple_results", label: "Multiple Results" },
]);

const BROWSE_CATALOGUE_NODE_TYPE = "browse_catalogue";

const STRING_COMPATIBLE_INPUT_TYPES = new Set(["text", "email", "phone"]);

export const createSearchCatalogueConfig = () => ({
  searchMode: "filtered",
  queryFieldKey: "",
  catalogId: null,
  maximumResults: SEARCH_CATALOGUE_DEFAULT_MAXIMUM_RESULTS,
});

const edgeSource = edge => edge?.sourceNodeId || edge?.source || "";
const edgeTarget = edge => edge?.targetNodeId || edge?.target || "";

export const canReachSearchCatalogueNode = (
  sourceNodeId,
  searchNodeId,
  edges
) => {
  const source = String(sourceNodeId || "");
  const target = String(searchNodeId || "");
  if (!source || !target || source === target) return false;

  const adjacency = new Map();
  (Array.isArray(edges) ? edges : []).forEach(edge => {
    const from = String(edgeSource(edge) || "");
    const to = String(edgeTarget(edge) || "");
    if (!from || !to) return;
    if (!adjacency.has(from)) adjacency.set(from, []);
    adjacency.get(from).push(to);
  });

  const pending = [source];
  const visited = new Set([source]);
  while (pending.length) {
    const current = pending.shift();
    for (const next of adjacency.get(current) || []) {
      if (next === target) return true;
      if (!visited.has(next)) {
        visited.add(next);
        pending.push(next);
      }
    }
  }
  return false;
};

export const getUpstreamBrowseCatalogueNodes = (
  nodes,
  edges,
  searchNodeId
) =>
  (Array.isArray(nodes) ? nodes : [])
    .filter(
      candidate =>
        String(candidate?.type || "").trim().toLowerCase() ===
          BROWSE_CATALOGUE_NODE_TYPE &&
        String(candidate?.id || "") !== String(searchNodeId || "") &&
        canReachSearchCatalogueNode(candidate?.id, searchNodeId, edges)
    )
    .map(candidate => ({
      id: String(candidate.id),
      label: String(
        candidate?.data?.label || "Browse Catalogue"
      ).trim(),
      catalogId:
        String(candidate?.data?.config?.catalogId || "").trim() || null,
    }));

export const getSearchCatalogueInputFields = nodes => {
  const fields = new Map();

  (Array.isArray(nodes) ? nodes : []).forEach(node => {
    if (String(node?.type || "").trim().toLowerCase() !== "input_capture") return;

    const config = node?.data?.config || {};
    const fieldKey = String(config.fieldKey || "").trim().toLowerCase();
    const inputType = String(config.inputType || "").trim().toLowerCase();
    if (!fieldKey) return;

    const existing = fields.get(fieldKey) || {
      fieldKey,
      inputTypes: [],
      question: String(
        config.question || node?.data?.label || "Input Capture"
      ).trim(),
    };
    if (!existing.inputTypes.includes(inputType)) existing.inputTypes.push(inputType);
    fields.set(fieldKey, existing);
  });

  return Array.from(fields.values())
    .filter(field =>
      field.inputTypes.length > 0 &&
      field.inputTypes.every(type => STRING_COMPATIBLE_INPUT_TYPES.has(type))
    )
    .map(field => ({
      ...field,
      inputType: field.inputTypes[0],
    }))
    .sort((left, right) => {
      const leftText = left.inputTypes.includes("text") ? 0 : 1;
      const rightText = right.inputTypes.includes("text") ? 0 : 1;
      return leftText - rightText || left.fieldKey.localeCompare(right.fieldKey);
    });
};

export const validateSearchCatalogueConfig = (
  config,
  nodes,
  edges,
  searchNodeId
) => {
  const searchMode = String(config?.searchMode || "filtered").trim().toLowerCase();
  const queryFieldKey = String(config?.queryFieldKey || "").trim();
  const categorySourceNodeId = String(
    config?.categorySourceNodeId || ""
  ).trim();
  const maximumResults = Number(config?.maximumResults);
  const catalogId = String(config?.catalogId || "").trim();

  if (!["filtered", "all"].includes(searchMode)) {
    return { isValid: false, message: "Select a valid search mode." };
  }
  if (searchMode === "filtered" && !queryFieldKey && !categorySourceNodeId) {
    return {
      isValid: false,
      reason: "search_input_required",
      message: "Select a query field, a Browse Catalogue source, or both.",
    };
  }
  if (searchMode === "all" && !catalogId) {
    return {
      isValid: false,
      reason: "catalogue_required_for_all",
      message: "Select a catalogue for all catalogue products.",
    };
  }
  if (
    categorySourceNodeId &&
    categorySourceNodeId === String(searchNodeId || "")
  ) {
    return {
      isValid: false,
      reason: "category_source_self_reference",
      message: "Search Catalogue cannot use itself as a category source.",
    };
  }
  if (categorySourceNodeId && Array.isArray(nodes)) {
    const sourceNode = nodes.find(
      candidate =>
        String(candidate?.id || "") === categorySourceNodeId
    );
    if (!sourceNode) {
      return {
        isValid: false,
        reason: "stale_category_source",
        message:
          "The selected Browse Catalogue source is no longer available in this flow.",
      };
    }
    if (
      String(sourceNode?.type || "").trim().toLowerCase() !==
      BROWSE_CATALOGUE_NODE_TYPE
    ) {
      return {
        isValid: false,
        reason: "wrong_category_source_type",
        message: "Category source must be a Browse Catalogue node.",
      };
    }
    if (
      !canReachSearchCatalogueNode(
        categorySourceNodeId,
        searchNodeId,
        edges
      )
    ) {
      return {
        isValid: false,
        reason: "category_source_not_upstream",
        message:
          "Category source must be an earlier reachable Browse Catalogue node.",
      };
    }
  }
  if (
    !Number.isInteger(maximumResults) ||
    maximumResults < SEARCH_CATALOGUE_MINIMUM_RESULTS ||
    maximumResults > SEARCH_CATALOGUE_MAXIMUM_RESULTS
  ) {
    return {
      isValid: false,
      message: `Maximum results must be between ${SEARCH_CATALOGUE_MINIMUM_RESULTS} and ${SEARCH_CATALOGUE_MAXIMUM_RESULTS}.`,
    };
  }
  if (
    catalogId &&
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(catalogId)
  ) {
    return { isValid: false, message: "Select a valid catalogue." };
  }

  return {
    isValid: true,
    message: "",
    normalized: {
      searchMode,
      queryFieldKey: searchMode === "all" ? "" : queryFieldKey,
      categorySourceNodeId:
        searchMode === "all" ? null : categorySourceNodeId || null,
      catalogId: catalogId || null,
      maximumResults,
    },
  };
};
